// ============================================
// Event Catalog — Single Source of Truth
//
// Every event type across all three event systems
// (Funnel, Platform, Patient) is defined here.
// Other files import from this catalog.
//
// Three domains:
//   1. Funnel   — Patient conversion analytics (client-side)
//   2. Platform — Cross-system regulatory/clinical/ops (event bus)
//   3. Patient  — Patient-facing communications (OpenLoop pipeline)
//
// Implements the building-agnostic EventEntry contract from
// eventContract.ts so this catalog is portable across buildings.
//
// OFFER ALIGNMENT:
//   Events map to the 6 Hemologix offer steps:
//   1. HOPE         — PAGE_VIEW, CTA_CLICK
//   2. FREE_ANALYSIS — LAB_UPLOAD, TRIAGE_START, TRIAGE_COMPLETE
//   3. EDUCATION    — PDF_DOWNLOADED
//   4. SMART_INTAKE — PROTOCOL_VIEWED, ACCOUNT_CREATED
//   5. THE_FORK     — CHECKOUT_STARTED
//   6. GET_THE_MEDS — PURCHASE_COMPLETE, ORDER_CONFIRMED, ORDER_SHIPPED,
//                     MEDICATION_ORDERED, MEDICATION_INSTRUCTIONS,
//                     REFILL_DUE, REFILL_REMINDER, REFILL_SUBMITTED
//   CLINICAL_SAFETY — FDA_*, INTERACTION_DISCOVERED, STUDY_RETRACTED,
//                     FORMULARY_DRIFT_DETECTED, BIOMARKER_RANGE_REVISED,
//                     GUIDELINE_UPDATED, POSTURE_REVERSAL
//   PLATFORM_OPS   — SYSTEM_HEALTH_ALERT, HEALTH_CHECK_FAILED,
//                     COST_BUDGET_BREACH, DEPENDENCY_VULNERABILITY,
//                     REMEDIATION_EXECUTED, TOS_COMPLIANCE_BREACH,
//                     DEBT_THRESHOLD_BREACH
//
// USE CASES:
//   UC-CAT-1: Event bus queries catalog by domain to route events
//             to domain-specific consumers.
//   UC-CAT-2: Safety checker queries catalog to validate event
//             severity before publishing.
//   UC-CAT-3: Admin dashboard groups events by offer step to show
//             conversion funnel health.
//   UC-CAT-4: Patient event processor reads priority and channel
//             maps to schedule communications.
// ============================================

import type { EventEntry } from './eventContract';

// Re-export EventSeverity from the contract (canonical definition)
export type { EventSeverity } from './eventContract';

// ── DOMAIN ──

export type EventDomain = 'funnel' | 'platform' | 'patient';

// ── FUNNEL EVENT TYPES (10) ──
// Collection: funnel_events
// Fire-and-forget conversion analytics

export type FunnelEventType =
  | 'PAGE_VIEW'
  | 'LAB_UPLOAD'
  | 'TRIAGE_START'
  | 'TRIAGE_COMPLETE'
  | 'PROTOCOL_VIEWED'
  | 'CHECKOUT_STARTED'
  | 'PURCHASE_COMPLETE'
  | 'ACCOUNT_CREATED'
  | 'PDF_DOWNLOADED'
  | 'CTA_CLICK';

// ── PLATFORM EVENT TYPES (25) ──
// Collection: event_bus
// Pub/sub for Promise Keeper, Master Formulator, Doc Ops

export type PlatformEventType =
  // Regulatory (Promise Keeper publishes)
  | 'FDA_RULE_CHANGE'
  | 'FDA_ENFORCEMENT_ACTION'
  | 'FDA_INGREDIENT_ACTION'
  | 'STATE_LAW_CHANGE'
  | 'DEA_TELEHEALTH_RULE'
  | 'CMS_REIMBURSEMENT_CHANGE'
  | 'HHS_POLICY_SHIFT'
  | 'POSTURE_REVERSAL'
  // Clinical (Master Formulator publishes)
  | 'INTERACTION_DISCOVERED'
  | 'STUDY_RETRACTED'
  | 'FORMULARY_DRIFT_DETECTED'
  | 'MEDICAL_DIRECTOR_EXPIRY'
  | 'BIOMARKER_RANGE_REVISED'
  | 'GUIDELINE_UPDATED'
  | 'XPRIZE_UPDATE'
  // Platform
  | 'COMPLIANCE_DEADLINE'
  | 'DOCUMENT_REGENERATION_REQUIRED'
  | 'COMMITMENT_BREACH_RISK'
  | 'SYSTEM_HEALTH_ALERT'
  // Phase 7 Ops Events
  | 'COST_BUDGET_BREACH'
  | 'DEPENDENCY_VULNERABILITY'
  | 'REMEDIATION_EXECUTED'
  | 'HEALTH_CHECK_FAILED'
  | 'TOS_COMPLIANCE_BREACH'
  | 'DEBT_THRESHOLD_BREACH';

// ── PATIENT EVENT TYPES (23) ──
// Collection: patient_events
// OpenLoop communication pipeline

export type PatientEventType =
  // High priority (10-9)
  | 'VISIT_DQ'
  | 'PROGRAM_DENIED'
  | 'VISIT_NO_SHOW'
  | 'APPOINTMENT_REMINDER'
  // Medium priority (8-5)
  | 'LABS_RESULTED'
  | 'MEDICATION_ORDERED'
  | 'VISIT_COMPLETED'
  | 'AVS_READY'
  | 'MEDICATION_INSTRUCTIONS'
  | 'ORDER_SHIPPED'
  | 'ORDER_CONFIRMED'
  | 'LABS_ORDERED'
  | 'PROGRAM_APPROVED'
  | 'REFILL_DUE'
  | 'REFILL_REMINDER'
  // Low priority (4-2)
  | 'APPOINTMENT_SCHEDULED'
  | 'APPOINTMENT_RESCHEDULED'
  | 'APPOINTMENT_CANCELLED'
  | 'INTAKE_SUBMITTED'
  | 'INTAKE_PENDING_REVIEW'
  | 'REFILL_SUBMITTED'
  | 'PROGRAM_PAUSED'
  | 'PROGRAM_CANCELLED';

// ── UNIFIED TYPE ──

export type HemologixEventType = FunnelEventType | PlatformEventType | PatientEventType;

// ── TYPED INTERFACES ──

/** Funnel event payload (client-side analytics) */
export interface FunnelEvent {
  event: FunnelEventType;
  metadata?: Record<string, string | number | boolean>;
}

/** Patient event payload (OpenLoop communication queue) */
export interface PatientEvent {
  eventId: string;
  eventType: PatientEventType;
  patientId: string;
  program: string;
  priority: number;
  channels: PatientEventChannel[];
  referenceData: Record<string, string>;
  status: 'queued' | 'processing' | 'delivered' | 'failed';
  deliveryStatus: Record<string, { status: string; sentAt?: string; error?: string }>;
  scheduledFor?: string;
  createdAt: string;
  sourceFunction: string;
}

export type PatientEventChannel = 'email' | 'sms' | 'push';

// ── CATALOG ENTRY ──

/** Hemologix-specific catalog entry implementing the building-agnostic contract */
export interface EventCatalogEntry extends EventEntry<HemologixEventType, EventDomain> {}

// ── EVENT CATALOG ──
// Metadata for every event type across all three systems.

export const EVENT_CATALOG: Record<HemologixEventType, EventCatalogEntry> = {
  // ── Funnel (10) ──
  PAGE_VIEW:          { type: 'PAGE_VIEW',          domain: 'funnel', label: 'Page View',          description: 'Landing page viewed',                         defaultSeverity: 'info' },
  LAB_UPLOAD:         { type: 'LAB_UPLOAD',         domain: 'funnel', label: 'Lab Upload',         description: 'Lab file uploaded for analysis',               defaultSeverity: 'info' },
  TRIAGE_START:       { type: 'TRIAGE_START',       domain: 'funnel', label: 'Triage Start',       description: 'Triage flow initiated',                        defaultSeverity: 'info' },
  TRIAGE_COMPLETE:    { type: 'TRIAGE_COMPLETE',    domain: 'funnel', label: 'Triage Complete',    description: 'Triage form completed',                        defaultSeverity: 'info' },
  PROTOCOL_VIEWED:    { type: 'PROTOCOL_VIEWED',    domain: 'funnel', label: 'Protocol Viewed',    description: 'Patient views their protocol',                 defaultSeverity: 'info' },
  CHECKOUT_STARTED:   { type: 'CHECKOUT_STARTED',   domain: 'funnel', label: 'Checkout Started',   description: 'Checkout flow initiated',                      defaultSeverity: 'info' },
  PURCHASE_COMPLETE:  { type: 'PURCHASE_COMPLETE',  domain: 'funnel', label: 'Purchase Complete',  description: 'Order successfully placed',                    defaultSeverity: 'info' },
  ACCOUNT_CREATED:    { type: 'ACCOUNT_CREATED',    domain: 'funnel', label: 'Account Created',    description: 'Patient account creation',                     defaultSeverity: 'info' },
  PDF_DOWNLOADED:     { type: 'PDF_DOWNLOADED',     domain: 'funnel', label: 'PDF Downloaded',     description: 'Protocol/report PDF exported',                 defaultSeverity: 'info' },
  CTA_CLICK:          { type: 'CTA_CLICK',          domain: 'funnel', label: 'CTA Click',          description: 'Call-to-action button clicked',                defaultSeverity: 'info' },

  // ── Platform — Regulatory (8) ──
  FDA_RULE_CHANGE:              { type: 'FDA_RULE_CHANGE',              domain: 'platform', label: 'FDA Rule Change',              description: 'FDA rule or policy change',                              defaultSeverity: 'high' },
  FDA_ENFORCEMENT_ACTION:       { type: 'FDA_ENFORCEMENT_ACTION',       domain: 'platform', label: 'FDA Enforcement Action',       description: 'FDA enforcement action issued',                          defaultSeverity: 'critical' },
  FDA_INGREDIENT_ACTION:        { type: 'FDA_INGREDIENT_ACTION',        domain: 'platform', label: 'FDA Ingredient Action',        description: 'FDA action on specific ingredient',                      defaultSeverity: 'critical' },
  STATE_LAW_CHANGE:             { type: 'STATE_LAW_CHANGE',             domain: 'platform', label: 'State Law Change',             description: 'State-level legal change affecting operations',           defaultSeverity: 'medium' },
  DEA_TELEHEALTH_RULE:          { type: 'DEA_TELEHEALTH_RULE',          domain: 'platform', label: 'DEA Telehealth Rule',          description: 'DEA telehealth prescribing rule change',                 defaultSeverity: 'high' },
  CMS_REIMBURSEMENT_CHANGE:     { type: 'CMS_REIMBURSEMENT_CHANGE',     domain: 'platform', label: 'CMS Reimbursement Change',     description: 'CMS reimbursement policy shift',                         defaultSeverity: 'medium' },
  HHS_POLICY_SHIFT:             { type: 'HHS_POLICY_SHIFT',             domain: 'platform', label: 'HHS Policy Shift',             description: 'HHS policy change',                                      defaultSeverity: 'medium' },
  POSTURE_REVERSAL:             { type: 'POSTURE_REVERSAL',             domain: 'platform', label: 'Posture Reversal',             description: 'Regulatory posture reversal — urgent re-evaluation',      defaultSeverity: 'critical' },

  // ── Platform — Clinical (7) ──
  INTERACTION_DISCOVERED:       { type: 'INTERACTION_DISCOVERED',       domain: 'platform', label: 'Drug Interaction Discovered',   description: 'New drug interaction found',                             defaultSeverity: 'high' },
  STUDY_RETRACTED:              { type: 'STUDY_RETRACTED',              domain: 'platform', label: 'Study Retracted',              description: 'Clinical study retracted',                               defaultSeverity: 'high' },
  FORMULARY_DRIFT_DETECTED:     { type: 'FORMULARY_DRIFT_DETECTED',     domain: 'platform', label: 'Formulary Drift Detected',     description: 'Product out of alignment with evidence',                 defaultSeverity: 'medium' },
  MEDICAL_DIRECTOR_EXPIRY:      { type: 'MEDICAL_DIRECTOR_EXPIRY',      domain: 'platform', label: 'Medical Director Expiry',      description: 'Medical director license expiring',                      defaultSeverity: 'high' },
  BIOMARKER_RANGE_REVISED:      { type: 'BIOMARKER_RANGE_REVISED',      domain: 'platform', label: 'Biomarker Range Revised',      description: 'Lab reference range updated',                            defaultSeverity: 'medium' },
  GUIDELINE_UPDATED:            { type: 'GUIDELINE_UPDATED',            domain: 'platform', label: 'Guideline Updated',            description: 'Clinical guideline updated',                             defaultSeverity: 'medium' },
  XPRIZE_UPDATE:                { type: 'XPRIZE_UPDATE',                domain: 'platform', label: 'XPRIZE Update',                description: 'XPRIZE program milestone',                               defaultSeverity: 'low' },

  // ── Platform — Ops (10) ──
  COMPLIANCE_DEADLINE:              { type: 'COMPLIANCE_DEADLINE',              domain: 'platform', label: 'Compliance Deadline',              description: 'Compliance action deadline approaching',          defaultSeverity: 'high' },
  DOCUMENT_REGENERATION_REQUIRED:   { type: 'DOCUMENT_REGENERATION_REQUIRED',   domain: 'platform', label: 'Document Regeneration Required',   description: 'Documents need regeneration due to upstream changes', defaultSeverity: 'high' },
  COMMITMENT_BREACH_RISK:           { type: 'COMMITMENT_BREACH_RISK',           domain: 'platform', label: 'Commitment Breach Risk',           description: 'SLA or obligation at risk of breach',             defaultSeverity: 'critical' },
  SYSTEM_HEALTH_ALERT:              { type: 'SYSTEM_HEALTH_ALERT',              domain: 'platform', label: 'System Health Alert',              description: 'System health degradation detected',              defaultSeverity: 'low' },
  COST_BUDGET_BREACH:               { type: 'COST_BUDGET_BREACH',               domain: 'platform', label: 'Cost Budget Breach',               description: 'Budget overage detected',                         defaultSeverity: 'low' },
  DEPENDENCY_VULNERABILITY:         { type: 'DEPENDENCY_VULNERABILITY',         domain: 'platform', label: 'Dependency Vulnerability',         description: 'Security vulnerability in dependency',            defaultSeverity: 'low' },
  REMEDIATION_EXECUTED:             { type: 'REMEDIATION_EXECUTED',             domain: 'platform', label: 'Remediation Executed',             description: 'Auto-remediation action completed',               defaultSeverity: 'low' },
  HEALTH_CHECK_FAILED:              { type: 'HEALTH_CHECK_FAILED',              domain: 'platform', label: 'Health Check Failed',              description: 'Health check failure detected',                   defaultSeverity: 'low' },
  TOS_COMPLIANCE_BREACH:            { type: 'TOS_COMPLIANCE_BREACH',            domain: 'platform', label: 'TOS Compliance Breach',            description: 'Terms of service violation detected',             defaultSeverity: 'low' },
  DEBT_THRESHOLD_BREACH:            { type: 'DEBT_THRESHOLD_BREACH',            domain: 'platform', label: 'Debt Threshold Breach',            description: 'Codebase debt exceeded threshold',                defaultSeverity: 'low' },

  // ── Patient (23) ──
  VISIT_DQ:                 { type: 'VISIT_DQ',                 domain: 'patient', label: 'Visit Disqualified',        description: 'Visit disqualified',                          defaultSeverity: 'high' },
  PROGRAM_DENIED:           { type: 'PROGRAM_DENIED',           domain: 'patient', label: 'Program Denied',            description: 'Program application denied',                  defaultSeverity: 'high' },
  VISIT_NO_SHOW:            { type: 'VISIT_NO_SHOW',            domain: 'patient', label: 'Visit No-Show',             description: 'Patient no-show for appointment',             defaultSeverity: 'high' },
  APPOINTMENT_REMINDER:     { type: 'APPOINTMENT_REMINDER',     domain: 'patient', label: 'Appointment Reminder',      description: 'Appointment reminder notification',           defaultSeverity: 'medium' },
  LABS_RESULTED:            { type: 'LABS_RESULTED',            domain: 'patient', label: 'Labs Resulted',             description: 'Lab results available',                       defaultSeverity: 'medium' },
  MEDICATION_ORDERED:       { type: 'MEDICATION_ORDERED',       domain: 'patient', label: 'Medication Ordered',        description: 'Medication order placed',                     defaultSeverity: 'medium' },
  VISIT_COMPLETED:          { type: 'VISIT_COMPLETED',          domain: 'patient', label: 'Visit Completed',           description: 'Visit completed successfully',                defaultSeverity: 'medium' },
  AVS_READY:                { type: 'AVS_READY',                domain: 'patient', label: 'AVS Ready',                 description: 'After-visit summary ready',                   defaultSeverity: 'medium' },
  MEDICATION_INSTRUCTIONS:  { type: 'MEDICATION_INSTRUCTIONS',  domain: 'patient', label: 'Medication Instructions',   description: 'Medication instructions available',           defaultSeverity: 'medium' },
  ORDER_SHIPPED:            { type: 'ORDER_SHIPPED',            domain: 'patient', label: 'Order Shipped',             description: 'Order shipped to patient',                    defaultSeverity: 'low' },
  ORDER_CONFIRMED:          { type: 'ORDER_CONFIRMED',          domain: 'patient', label: 'Order Confirmed',           description: 'Order confirmed by vendor',                   defaultSeverity: 'low' },
  LABS_ORDERED:             { type: 'LABS_ORDERED',             domain: 'patient', label: 'Labs Ordered',              description: 'Lab tests ordered',                           defaultSeverity: 'low' },
  PROGRAM_APPROVED:         { type: 'PROGRAM_APPROVED',         domain: 'patient', label: 'Program Approved',          description: 'Program application approved',                defaultSeverity: 'low' },
  REFILL_DUE:               { type: 'REFILL_DUE',               domain: 'patient', label: 'Refill Due',                description: 'Refill due for medication',                   defaultSeverity: 'low' },
  REFILL_REMINDER:          { type: 'REFILL_REMINDER',          domain: 'patient', label: 'Refill Reminder',           description: 'Refill reminder notification',                defaultSeverity: 'low' },
  APPOINTMENT_SCHEDULED:    { type: 'APPOINTMENT_SCHEDULED',    domain: 'patient', label: 'Appointment Scheduled',     description: 'Appointment booked',                          defaultSeverity: 'info' },
  APPOINTMENT_RESCHEDULED:  { type: 'APPOINTMENT_RESCHEDULED',  domain: 'patient', label: 'Appointment Rescheduled',   description: 'Appointment rescheduled',                     defaultSeverity: 'info' },
  APPOINTMENT_CANCELLED:    { type: 'APPOINTMENT_CANCELLED',    domain: 'patient', label: 'Appointment Cancelled',     description: 'Appointment cancelled',                       defaultSeverity: 'info' },
  INTAKE_SUBMITTED:         { type: 'INTAKE_SUBMITTED',         domain: 'patient', label: 'Intake Submitted',          description: 'Intake form submitted',                       defaultSeverity: 'info' },
  INTAKE_PENDING_REVIEW:    { type: 'INTAKE_PENDING_REVIEW',    domain: 'patient', label: 'Intake Pending Review',     description: 'Intake pending provider review',              defaultSeverity: 'info' },
  REFILL_SUBMITTED:         { type: 'REFILL_SUBMITTED',         domain: 'patient', label: 'Refill Submitted',          description: 'Refill request submitted',                    defaultSeverity: 'info' },
  PROGRAM_PAUSED:           { type: 'PROGRAM_PAUSED',           domain: 'patient', label: 'Program Paused',            description: 'Program paused',                              defaultSeverity: 'info' },
  PROGRAM_CANCELLED:        { type: 'PROGRAM_CANCELLED',        domain: 'patient', label: 'Program Cancelled',         description: 'Program cancelled',                           defaultSeverity: 'info' },
};

// ── PATIENT EVENT PRIORITY MAP ──
// Higher number = processed first in each batch

export const PATIENT_EVENT_PRIORITY: Record<PatientEventType, number> = {
  VISIT_DQ: 10,
  PROGRAM_DENIED: 10,
  VISIT_NO_SHOW: 9,
  APPOINTMENT_REMINDER: 9,
  LABS_RESULTED: 8,
  MEDICATION_ORDERED: 8,
  VISIT_COMPLETED: 7,
  AVS_READY: 7,
  MEDICATION_INSTRUCTIONS: 7,
  ORDER_SHIPPED: 6,
  ORDER_CONFIRMED: 6,
  LABS_ORDERED: 6,
  PROGRAM_APPROVED: 5,
  REFILL_DUE: 5,
  REFILL_REMINDER: 5,
  APPOINTMENT_SCHEDULED: 4,
  APPOINTMENT_RESCHEDULED: 4,
  APPOINTMENT_CANCELLED: 4,
  INTAKE_SUBMITTED: 3,
  INTAKE_PENDING_REVIEW: 3,
  REFILL_SUBMITTED: 3,
  PROGRAM_PAUSED: 2,
  PROGRAM_CANCELLED: 2,
};

// ── PATIENT EVENT CHANNEL MAP ──

export const PATIENT_EVENT_CHANNELS: Record<PatientEventType, PatientEventChannel[]> = {
  INTAKE_SUBMITTED: ['email'],
  INTAKE_PENDING_REVIEW: ['email'],
  APPOINTMENT_SCHEDULED: ['email', 'sms'],
  APPOINTMENT_REMINDER: ['email', 'sms'],
  APPOINTMENT_RESCHEDULED: ['email', 'sms'],
  APPOINTMENT_CANCELLED: ['email', 'sms'],
  VISIT_COMPLETED: ['email'],
  VISIT_DQ: ['email'],
  VISIT_NO_SHOW: ['email', 'sms'],
  MEDICATION_ORDERED: ['email'],
  MEDICATION_INSTRUCTIONS: ['email'],
  LABS_ORDERED: ['email'],
  LABS_RESULTED: ['email', 'push'],
  AVS_READY: ['email'],
  ORDER_CONFIRMED: ['email'],
  ORDER_SHIPPED: ['email', 'sms'],
  REFILL_REMINDER: ['email', 'sms'],
  REFILL_DUE: ['email', 'sms'],
  REFILL_SUBMITTED: ['email'],
  PROGRAM_APPROVED: ['email', 'sms'],
  PROGRAM_DENIED: ['email'],
  PROGRAM_PAUSED: ['email'],
  PROGRAM_CANCELLED: ['email'],
};

// ── OFFER STEP ALIGNMENT ──
// Maps each event to the offer step it serves.
// Clinical safety and platform ops events don't map to patient-facing
// offer steps — they protect the system that delivers the offer.

export type OfferStep =
  | 'HOPE'
  | 'FREE_ANALYSIS'
  | 'EDUCATION'
  | 'SMART_INTAKE'
  | 'THE_FORK'
  | 'GET_THE_MEDS'
  | 'CLINICAL_SAFETY'
  | 'PLATFORM_OPS';

export const EVENT_OFFER_STEP_MAP: Record<HemologixEventType, OfferStep> = {
  // Funnel → Offer steps
  PAGE_VIEW: 'HOPE',
  CTA_CLICK: 'HOPE',
  LAB_UPLOAD: 'FREE_ANALYSIS',
  TRIAGE_START: 'FREE_ANALYSIS',
  TRIAGE_COMPLETE: 'FREE_ANALYSIS',
  PDF_DOWNLOADED: 'EDUCATION',
  PROTOCOL_VIEWED: 'SMART_INTAKE',
  ACCOUNT_CREATED: 'SMART_INTAKE',
  CHECKOUT_STARTED: 'THE_FORK',
  PURCHASE_COMPLETE: 'GET_THE_MEDS',

  // Platform — Regulatory/Clinical → Clinical safety
  FDA_RULE_CHANGE: 'CLINICAL_SAFETY',
  FDA_ENFORCEMENT_ACTION: 'CLINICAL_SAFETY',
  FDA_INGREDIENT_ACTION: 'CLINICAL_SAFETY',
  STATE_LAW_CHANGE: 'CLINICAL_SAFETY',
  DEA_TELEHEALTH_RULE: 'CLINICAL_SAFETY',
  CMS_REIMBURSEMENT_CHANGE: 'CLINICAL_SAFETY',
  HHS_POLICY_SHIFT: 'CLINICAL_SAFETY',
  POSTURE_REVERSAL: 'CLINICAL_SAFETY',
  INTERACTION_DISCOVERED: 'CLINICAL_SAFETY',
  STUDY_RETRACTED: 'CLINICAL_SAFETY',
  FORMULARY_DRIFT_DETECTED: 'CLINICAL_SAFETY',
  MEDICAL_DIRECTOR_EXPIRY: 'CLINICAL_SAFETY',
  BIOMARKER_RANGE_REVISED: 'CLINICAL_SAFETY',
  GUIDELINE_UPDATED: 'CLINICAL_SAFETY',
  XPRIZE_UPDATE: 'CLINICAL_SAFETY',

  // Platform — Ops → Platform ops
  COMPLIANCE_DEADLINE: 'PLATFORM_OPS',
  DOCUMENT_REGENERATION_REQUIRED: 'PLATFORM_OPS',
  COMMITMENT_BREACH_RISK: 'PLATFORM_OPS',
  SYSTEM_HEALTH_ALERT: 'PLATFORM_OPS',
  COST_BUDGET_BREACH: 'PLATFORM_OPS',
  DEPENDENCY_VULNERABILITY: 'PLATFORM_OPS',
  REMEDIATION_EXECUTED: 'PLATFORM_OPS',
  HEALTH_CHECK_FAILED: 'PLATFORM_OPS',
  TOS_COMPLIANCE_BREACH: 'PLATFORM_OPS',
  DEBT_THRESHOLD_BREACH: 'PLATFORM_OPS',

  // Patient → Offer steps (post-fork communication)
  VISIT_DQ: 'THE_FORK',
  PROGRAM_DENIED: 'THE_FORK',
  VISIT_NO_SHOW: 'THE_FORK',
  APPOINTMENT_REMINDER: 'SMART_INTAKE',
  LABS_RESULTED: 'FREE_ANALYSIS',
  MEDICATION_ORDERED: 'GET_THE_MEDS',
  VISIT_COMPLETED: 'SMART_INTAKE',
  AVS_READY: 'SMART_INTAKE',
  MEDICATION_INSTRUCTIONS: 'GET_THE_MEDS',
  ORDER_SHIPPED: 'GET_THE_MEDS',
  ORDER_CONFIRMED: 'GET_THE_MEDS',
  LABS_ORDERED: 'FREE_ANALYSIS',
  PROGRAM_APPROVED: 'THE_FORK',
  REFILL_DUE: 'GET_THE_MEDS',
  REFILL_REMINDER: 'GET_THE_MEDS',
  APPOINTMENT_SCHEDULED: 'SMART_INTAKE',
  APPOINTMENT_RESCHEDULED: 'SMART_INTAKE',
  APPOINTMENT_CANCELLED: 'SMART_INTAKE',
  INTAKE_SUBMITTED: 'SMART_INTAKE',
  INTAKE_PENDING_REVIEW: 'SMART_INTAKE',
  REFILL_SUBMITTED: 'GET_THE_MEDS',
  PROGRAM_PAUSED: 'THE_FORK',
  PROGRAM_CANCELLED: 'THE_FORK',
};

// ── HELPERS ──

/** Get all event types for a domain */
export function getEventsByDomain(domain: EventDomain): EventCatalogEntry[] {
  return Object.values(EVENT_CATALOG).filter((e) => e.domain === domain);
}

/** Look up a catalog entry by event type */
export function getEventEntry(type: HemologixEventType): EventCatalogEntry {
  return EVENT_CATALOG[type];
}

/** Get all event types that serve a specific offer step */
export function getEventsByOfferStep(step: OfferStep): EventCatalogEntry[] {
  return (Object.entries(EVENT_OFFER_STEP_MAP) as [HemologixEventType, OfferStep][])
    .filter(([, s]) => s === step)
    .map(([type]) => EVENT_CATALOG[type]);
}
